<template>
  <button
    type="button"
    :class="['toggle-button', { 'toggle-button--checked': modelValue, 'toggle-button--disabled': disabled }]"
    :disabled="disabled"
    :style="buttonStyle"
    @click="toggle"
    :aria-pressed="modelValue"
  >
    <span class="toggle-button__track" :style="trackStyle">
      <span v-if="currentLabel" class="toggle-button__label" :style="labelStyle">{{ currentLabel }}</span>
      <span class="toggle-button__thumb" :style="thumbStyle"></span>
    </span>
  </button>
</template>

<script>
export default {
  name: 'ToggleButton',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    // legacy v-model alias
    value: {
      type: Boolean,
      default: undefined
    },
    sync: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    labels: {
      type: [Boolean, Object],
      default: false
    },
    width: {
      type: Number,
      default: 60
    },
    height: {
      type: Number,
      default: 22
    },
    margin: {
      type: Number,
      default: 3
    },
    speed: {
      type: Number,
      default: 300
    },
    fontSize: {
      type: Number,
      default: 10
    },
    color: {
      type: [String, Object],
      default: () => ({ checked: '#75C791', unchecked: '#bfcbd9', disabled: '#d1dce4' })
    }
  },
  emits: ['update:modelValue', 'change', 'input'],
  computed: {
    isChecked () {
      return this.value !== undefined ? this.value : this.modelValue
    },
    currentLabel () {
      if (!this.labels) return ''
      if (typeof this.labels === 'object') {
        return this.isChecked ? (this.labels.checked || '') : (this.labels.unchecked || '')
      }
      return ''
    },
    currentColor () {
      const c = this.color
      if (typeof c === 'string') return c
      if (this.disabled && c.disabled) return c.disabled
      return this.isChecked ? (c.checked || '#75C791') : (c.unchecked || '#bfcbd9')
    },
    thumbSize () {
      return this.height - this.margin * 2
    },
    thumbOffset () {
      return this.isChecked ? this.width - this.thumbSize - this.margin : this.margin
    },
    buttonStyle () {
      return {
        display: 'inline-flex',
        alignItems: 'center',
        cursor: this.disabled ? 'not-allowed' : 'pointer',
        verticalAlign: 'middle',
        userSelect: 'none',
        background: 'none',
        border: 'none',
        padding: '0',
        gap: '4px'
      }
    },
    trackStyle () {
      return {
        display: 'inline-block',
        position: 'relative',
        width: this.width + 'px',
        height: this.height + 'px',
        borderRadius: this.height / 2 + 'px',
        backgroundColor: this.currentColor,
        transition: `background-color ${this.speed}ms ease`,
        flexShrink: '0'
      }
    },
    thumbStyle () {
      return {
        position: 'absolute',
        top: this.margin + 'px',
        left: this.thumbOffset + 'px',
        width: this.thumbSize + 'px',
        height: this.thumbSize + 'px',
        borderRadius: '50%',
        backgroundColor: '#fff',
        transition: `left ${this.speed}ms ease`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
      }
    },
    labelStyle () {
      return {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        left: this.isChecked ? this.margin + 'px' : (this.thumbSize + this.margin * 2) + 'px',
        right: this.isChecked ? (this.thumbSize + this.margin * 2) + 'px' : this.margin + 'px',
        fontSize: this.fontSize + 'px',
        color: '#fff',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textAlign: 'center',
        pointerEvents: 'none',
        transition: `left ${this.speed}ms ease, right ${this.speed}ms ease`
      }
    }
  },
  methods: {
    toggle () {
      if (this.disabled) return
      const newVal = !this.isChecked
      this.$emit('update:modelValue', newVal)
      this.$emit('input', newVal)
      this.$emit('change', { value: newVal })
    }
  }
}
</script>
